import type { Editor } from '@tiptap/react';

/** Document position at the top of the editor's visible area. */
export function getDocPosAtViewportTop(editor: Editor): number | null {
    const paneScrollEl = paneScrollElement(editor);
    if (!paneScrollEl) {
        return null;
    }
    const rect = paneScrollEl.getBoundingClientRect();
    const coords = editor.view.posAtCoords({ left: rect.left + rect.width / 2, top: rect.top + 4 });
    return coords?.pos ?? null;
}

export function paneScrollElement(editor: Editor): HTMLElement | null {
    return editor.view.dom.closest('[data-split-pane-scroll="true"]');
}

/** Scroll a pane's inner scroll container so `pos` sits at the top of the visible area. */
export function scrollPaneToDocPosAtViewportTop(editor: Editor, pos: number): void {
    const scrollEl = paneScrollElement(editor);
    if (!scrollEl) {
        return;
    }
    const coords = editor.view.coordsAtPos(pos);
    const scrollRect = scrollEl.getBoundingClientRect();
    scrollEl.scrollTop += coords.top - scrollRect.top - 4;
}

export function maxScrollTop(el: HTMLElement): number {
    return Math.max(0, el.scrollHeight - el.clientHeight);
}

export function getScrollRatio(el: HTMLElement): number {
    const max = maxScrollTop(el);
    if (max === 0) {
        return 0;
    }
    return el.scrollTop / max;
}

export function averageScrollRatio(els: HTMLElement[]): number {
    if (els.length === 0) {
        return 0;
    }
    return els.reduce((sum, el) => sum + getScrollRatio(el), 0) / els.length;
}

/**
 * Clamp a proposed scroll delta so every element stays within `[0, maxScrollTop]`.
 * Positions are the start tops before applying the delta.
 */
export function clampScrollDelta(scrollTops: number[], maxScrollTops: number[], delta: number): number {
    if (delta === 0 || scrollTops.length === 0) {
        return 0;
    }

    let allowed = delta;
    for (let i = 0; i < scrollTops.length; i++) {
        const top = scrollTops[i] ?? 0;
        const max = maxScrollTops[i] ?? 0;
        if (delta > 0) {
            allowed = Math.min(allowed, max - top);
        } else {
            allowed = Math.max(allowed, -top);
        }
    }
    return Object.is(allowed, -0) ? 0 : allowed;
}

let isApplyingScrollSync = false;

export function isScrollSyncApplying(): boolean {
    return isApplyingScrollSync;
}

export function withScrollSyncApply(fn: () => void): void {
    isApplyingScrollSync = true;
    try {
        fn();
    } finally {
        isApplyingScrollSync = false;
    }
}

/** Set every pane to the average of their current scroll ratios. */
export function syncScrollPositionsByAverageRatio(scrollEls: HTMLElement[]): void {
    if (scrollEls.length === 0) {
        return;
    }
    const ratio = averageScrollRatio(scrollEls);
    withScrollSyncApply(() => {
        for (const el of scrollEls) {
            el.scrollTop = ratio * maxScrollTop(el);
        }
    });
}

/**
 * Attach scroll listeners that keep panes moving by the same clamped delta while locked.
 * Returns a cleanup function.
 */
export function attachDeltaScrollSync(scrollEls: HTMLElement[]): () => void {
    if (scrollEls.length <= 1) {
        return () => {};
    }

    const lastTops = scrollEls.map(el => el.scrollTop);

    const onScroll = (initiatorIndex: number) => {
        const initiator = scrollEls[initiatorIndex];
        if (!initiator) {
            return;
        }

        // Keep tracked tops current during programmatic applies so the next user scroll
        // computes delta from the real positions (e.g. Sync or preview-exit).
        if (isApplyingScrollSync) {
            lastTops[initiatorIndex] = initiator.scrollTop;
            return;
        }

        const intendedDelta = initiator.scrollTop - (lastTops[initiatorIndex] ?? 0);
        if (intendedDelta === 0) {
            return;
        }

        const maxTops = scrollEls.map(maxScrollTop);
        const clamped = clampScrollDelta(lastTops, maxTops, intendedDelta);

        withScrollSyncApply(() => {
            for (let i = 0; i < scrollEls.length; i++) {
                const el = scrollEls[i];
                if (!el) {
                    continue;
                }
                el.scrollTop = (lastTops[i] ?? 0) + clamped;
                lastTops[i] = el.scrollTop;
            }
        });
    };

    const listeners = scrollEls.map((el, index) => {
        const handler = () => onScroll(index);
        el.addEventListener('scroll', handler, { passive: true });
        return { el, handler };
    });

    return () => {
        for (const { el, handler } of listeners) {
            el.removeEventListener('scroll', handler);
        }
    };
}
